/**
 * Her portrait.
 *
 * Drawn, not photographed. That is a limit of what can be authored here rather
 * than a style preference, and it shapes every decision below: since it cannot
 * be a photograph, it is built to be a *good drawing* — layered shading instead
 * of flat fills, individual lashes and brow hairs instead of two strokes, a
 * limbal ring and striations in the iris, a cupid's bow and a gloss line on the
 * lip. Those are the details that separate a portrait from clipart, and they
 * are what has to carry it at 288px.
 *
 * The frame is a 3 × 2 landscape card — 288 × 192 CSS pixels, which is three
 * inches by two at the 96 px/inch that CSS defines an inch to be. A ruler held
 * against the glass will disagree on a high-density display, because the
 * operating system scales that; a web page has no way to know the physical size
 * of the panel it is drawn on.
 *
 * What makes her read as present, in order of how much each one earns:
 *
 *   the eyes track the pointer     she looks at you, not past you
 *   she blinks on her own          irregularly — regular blinking is eerie
 *   the head drifts and breathes   a face that holds still reads as dead
 *   the lips move on real speech   driven by the utterance, not a timer
 */
import { useEffect, useRef, useState } from "react";
import s from "./avatar.module.css";
import type { MarkState } from "./types";
import { t } from "../i18n";

const SAYS: Record<MarkState, string> = {
  idle: "Sky, ready",
  listening: "Sky, listening",
  thinking: "Sky, working",
  speaking: "Sky, speaking",
};

/** Pointer tracking. Returns roughly -1..1 on each axis, relative to this card. */
function useGaze(ref: React.RefObject<SVGSVGElement | null>, active: boolean) {
  const [gaze, setGaze] = useState({ x: 0, y: 0 });
  useEffect(() => {
    if (!active) return;
    let frame = 0;
    const onMove = (e: PointerEvent) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        /* Divided by a generous span so the eyes drift rather than snap; a
           pupil against the corner of the eye looks startled, not attentive. */
        const x = Math.max(-1, Math.min(1, (e.clientX - (r.left + r.width / 2)) / (r.width * 2.2)));
        const y = Math.max(-1, Math.min(1, (e.clientY - (r.top + r.height / 2)) / (r.height * 2.6)));
        setGaze({ x, y });
      });
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [active, ref]);
  return gaze;
}

/** Blinks at uneven intervals. A metronome blink is worse than none. */
function useBlink(active: boolean) {
  const [shut, setShut] = useState(false);
  useEffect(() => {
    if (!active) return;
    let timer: number;
    const schedule = () => {
      timer = window.setTimeout(
        () => {
          setShut(true);
          window.setTimeout(() => setShut(false), 115);
          schedule();
        },
        2400 + Math.random() * 4600,
      );
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [active]);
  return shut;
}

/**
 * Lashes along the lid arc, short and raked outward.
 *
 * Drawn from the curve rather than from a list of offsets: the first attempt
 * spaced them by x alone, which piled three of them on top of each other at the
 * outer corner and read as a smudge rather than as lashes.
 */
function Lashes({ cx, side }: { cx: number; side: 1 | -1 }) {
  return (
    <g stroke="#171120" strokeWidth="1.2" strokeLinecap="round" fill="none">
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const t = i / 5;
        const x = cx - 9.5 + t * 19;
        const y = 74.2 - Math.sin(t * Math.PI) * 2.1;
        /* Longest at the outer corner, which is where a lash line is heaviest. */
        const outward = side > 0 ? t : 1 - t;
        const len = 2.2 + outward * 2.6;
        return <path key={i} d={`M${x} ${y}l${side * outward * 1.8} ${-len}`} />;
      })}
    </g>
  );
}

export function Avatar({
  state,
  /** Card width in px; the height follows at 2:3. */
  width = 288,
  alive = true,
  className = "",
}: {
  state: MarkState;
  width?: number;
  alive?: boolean;
  className?: string;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const live = alive && width >= 120;
  const gaze = useGaze(ref, live);
  const shut = useBlink(live);

  /*
   * In the dock she is 36px across, and the whole 3:2 scene at that size is an
   * unreadable smudge — a dark shape in a blue box. So below the threshold the
   * card crops square to her face, which stays recognisable at 24px. Same
   * drawing, different framing; nothing is redrawn for the small case.
   */
  const small = width < 120;
  const box = small
    ? { view: "104 24 92 92", w: width, h: width }
    : { view: "0 0 300 200", w: width, h: Math.round((width * 2) / 3) };

  return (
    <svg
      ref={ref}
      className={`${s.avatar} ${small ? s.small : ""} ${s[`is_${state}`]} ${shut ? s.blink : ""} ${className}`}
      width={box.w}
      height={box.h}
      viewBox={box.view}
      role="img"
      aria-label={t(SAYS[state])}
    >
      <defs>
        {/* the field behind her: blue, deepening at the edges */}
        <radialGradient id="av-bg" cx="50%" cy="42%" r="72%">
          <stop offset="0%" stopColor="#1f66a8" />
          <stop offset="52%" stopColor="#0f3f73" />
          <stop offset="100%" stopColor="#07233f" />
        </radialGradient>

        {/* fair skin, lit cool from the right and warm from the left */}
        <linearGradient id="av-skin" x1="22%" y1="4%" x2="82%" y2="96%">
          <stop offset="0%" stopColor="#fdeee5" />
          <stop offset="38%" stopColor="#f6ddd0" />
          <stop offset="72%" stopColor="#e7c4b4" />
          <stop offset="100%" stopColor="#c79e90" />
        </linearGradient>
        <linearGradient id="av-neck" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#c9a294" />
          <stop offset="60%" stopColor="#e7c6b6" />
          <stop offset="100%" stopColor="#d9b3a3" />
        </linearGradient>

        {/* long black hair: not flat black, or it reads as a hole in the card */}
        <linearGradient id="av-hair" x1="18%" y1="0%" x2="86%" y2="100%">
          <stop offset="0%" stopColor="#241f2e" />
          <stop offset="42%" stopColor="#120e19" />
          <stop offset="100%" stopColor="#06040a" />
        </linearGradient>
        <linearGradient id="av-hair2" x1="0%" y1="0%" x2="100%" y2="60%">
          <stop offset="0%" stopColor="#2e2740" />
          <stop offset="100%" stopColor="#0a0812" />
        </linearGradient>

        {/* blue eyes */}
        <radialGradient id="av-iris" cx="50%" cy="38%" r="62%">
          <stop offset="0%" stopColor="#bfefff" />
          <stop offset="34%" stopColor="#54b4e8" />
          <stop offset="74%" stopColor="#1c6fae" />
          <stop offset="100%" stopColor="#0b3a63" />
        </radialGradient>

        {/* red lips */}
        <linearGradient id="av-lip-top" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#a01f2c" />
          <stop offset="100%" stopColor="#c9303c" />
        </linearGradient>
        <linearGradient id="av-lip-bot" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#e2434c" />
          <stop offset="62%" stopColor="#c02a36" />
          <stop offset="100%" stopColor="#8d1c26" />
        </linearGradient>

        <linearGradient id="av-rim" x1="100%" y1="10%" x2="20%" y2="90%">
          <stop offset="0%" stopColor="#bfe9ff" stopOpacity="0.95" />
          <stop offset="55%" stopColor="#6cc4f5" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#6cc4f5" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="av-shoulder" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#16355a" />
          <stop offset="100%" stopColor="#070f1c" />
        </linearGradient>

        <clipPath id="av-card">
          <rect x="2" y="2" width="296" height="196" rx="12" />
        </clipPath>
        <filter id="av-glow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* cheek, jaw and neck shadow: soft, never a hard edge */}
        <filter id="av-soft" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4.5" />
        </filter>
        <filter id="av-soft2" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" />
        </filter>
      </defs>

      <g clipPath="url(#av-card)">
        <rect x="2" y="2" width="296" height="196" fill="url(#av-bg)" />

        {/* ── black grid on the blue field ──────────────────────────── */}
        <g className={s.grid} stroke="#05080f" fill="none">
          {/* the wall */}
          {Array.from({ length: 13 }, (_, i) => i * 16).map((y) => (
            <line key={`h${y}`} x1="0" y1={y} x2="300" y2={y} strokeWidth="1.1" opacity="0.42" />
          ))}
          {Array.from({ length: 20 }, (_, i) => i * 16).map((x) => (
            <line key={`v${x}`} x1={x} y1="0" x2={x} y2="126" strokeWidth="1.1" opacity="0.42" />
          ))}
          {/* the floor, receding */}
          {[130, 140, 152, 167, 185, 206].map((y, i) => (
            <line key={`f${y}`} x1="-30" y1={y} x2="330" y2={y} strokeWidth={1 + i * 0.22} opacity="0.5" />
          ))}
          {[-230, -140, -60, 10, 74, 150, 226, 290, 360, 440, 530].map((x) => (
            <line key={`c${x}`} x1={x} y1="210" x2={150 + (x - 150) * 0.12} y2="127" strokeWidth="1.1" opacity="0.45" />
          ))}
        </g>
        {/* the horizon burn, which is what turns a grid into distance */}
        <ellipse cx="150" cy="127" rx="160" ry="18" fill="#3f92d8" opacity="0.3" filter="url(#av-soft)" />

        {/* ── her ────────────────────────────────────────────────────── */}
        <g className={s.figure}>
          {/* long black hair, behind everything */}
          <path
            d="M150 12c-42 0-66 27-68 65-1 24 0 44-3 64-2 18-5 33-10 61l30 4c-6-34-6-68-3-94 2-22 5-39 10-50 11 22 30 35 46 35s35-13 46-35c5 11 8 28 10 50 3 26 3 60-3 94l30-4c-5-28-8-43-10-61-3-20-2-40-3-64-2-38-26-65-68-65z"
            fill="url(#av-hair)"
          />
          {/* strands catching the rim light, so the mass is not one silhouette */}
          <g stroke="#4a4463" fill="none" opacity="0.5" strokeLinecap="round">
            <path d="M96 96c-2 30-1 60 4 86" strokeWidth="1.4" />
            <path d="M106 86c-4 32-3 64 2 88" strokeWidth="1" opacity="0.7" />
            <path d="M204 96c2 30 1 60-4 86" strokeWidth="1.4" />
            <path d="M194 86c4 32 3 64-2 88" strokeWidth="1" opacity="0.7" />
          </g>

          {/* shoulders */}
          <path d="M62 208c5-27 28-42 52-49l36-9 36 9c24 7 47 22 52 49z" fill="url(#av-shoulder)" />
          <path
            d="M62 208c5-27 28-42 52-49l36-9 36 9c24 7 47 22 52 49"
            fill="none"
            stroke="#6cc4f5"
            strokeWidth="1.1"
            opacity="0.6"
          />
          <path d="M124 158c11 15 41 15 52 0" fill="none" stroke="#8fd9ff" strokeWidth="1.5" opacity="0.75" />
          <circle className={s.temple} cx="180" cy="172" r="2.1" fill="#9fe4ff" filter="url(#av-glow)" />

          <g className={s.head}>
            {/* neck, with the shadow the jaw throws across it */}
            <path d="M134 108h32v26c0 11-32 11-32 0z" fill="url(#av-neck)" />
            <ellipse cx="150" cy="114" rx="19" ry="7" fill="#a97f72" opacity="0.5" filter="url(#av-soft2)" />

            {/* face */}
            <path
              d="M150 28c25 0 41 16 41 40 0 10-1 21-3 30-3 14-9 25-17 32-6 5-13 8-21 8s-15-3-21-8c-8-7-14-18-17-32-2-9-3-20-3-30 0-24 16-40 41-40z"
              fill="url(#av-skin)"
            />

            {/* modelling: temple hollow, cheekbone, under-cheek, chin */}
            <ellipse cx="119" cy="78" rx="8" ry="13" fill="#c69a8b" opacity="0.35" filter="url(#av-soft)" />
            <ellipse cx="181" cy="78" rx="8" ry="13" fill="#c69a8b" opacity="0.28" filter="url(#av-soft)" />
            <ellipse cx="126" cy="92" rx="10" ry="7" fill="#fdf1e9" opacity="0.4" filter="url(#av-soft2)" />
            <ellipse cx="174" cy="92" rx="10" ry="7" fill="#fdf1e9" opacity="0.34" filter="url(#av-soft2)" />
            <ellipse cx="123" cy="104" rx="9" ry="8" fill="#c08d7e" opacity="0.3" filter="url(#av-soft)" />
            <ellipse cx="177" cy="104" rx="9" ry="8" fill="#c08d7e" opacity="0.26" filter="url(#av-soft)" />
            <ellipse cx="150" cy="46" rx="22" ry="12" fill="#fff6ef" opacity="0.3" filter="url(#av-soft)" />
            <ellipse cx="150" cy="124" rx="9" ry="5" fill="#fff4ec" opacity="0.35" filter="url(#av-soft2)" />
            {/* blush */}
            <ellipse cx="124" cy="97" rx="9" ry="6" fill="#e79a92" opacity="0.28" filter="url(#av-soft)" />
            <ellipse cx="176" cy="97" rx="9" ry="6" fill="#e79a92" opacity="0.24" filter="url(#av-soft)" />

            {/* rim light: the strongest single cue that she is lit rather than drawn */}
            <path
              d="M191 68c0 10-1 21-3 30-3 14-9 25-17 32-6 5-13 8-21 8"
              fill="none"
              stroke="url(#av-rim)"
              strokeWidth="4.5"
              filter="url(#av-glow)"
            />
            <path
              d="M109 68c0 10 1 21 3 30 3 14 9 25 17 32"
              fill="none"
              stroke="#7cc8f0"
              strokeWidth="2.2"
              opacity="0.32"
            />

            {/* brows, drawn as hairs rather than as a stroke */}
            <g stroke="#191325" strokeLinecap="round" fill="none">
              {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                <path
                  key={`bl${i}`}
                  d={`M${122 + i * 3.6} ${64 - i * 0.75}l2.8 ${-1.4 - i * 0.12}`}
                  strokeWidth={2.4 - i * 0.18}
                />
              ))}
              {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                <path
                  key={`br${i}`}
                  d={`M${178 - i * 3.6} ${64 - i * 0.75}l-2.8 ${-1.4 - i * 0.12}`}
                  strokeWidth={2.4 - i * 0.18}
                />
              ))}
            </g>

            {/* ── eyes ─────────────────────────────────────────────── */}
            {[
              { cx: 132, side: -1 as const },
              { cx: 168, side: 1 as const },
            ].map((eye) => (
              <g key={eye.cx}>
                <ellipse cx={eye.cx} cy="75" rx="13" ry="8" fill="#c69a8b" opacity="0.4" filter="url(#av-soft2)" />
                <ellipse cx={eye.cx} cy="76" rx="11" ry="6.6" fill="#fbfdff" />
                <ellipse cx={eye.cx} cy="73.8" rx="11" ry="3.6" fill="#cfdeea" opacity="0.55" />
                <circle cx={eye.cx + eye.side * 9.5} cy="77.5" r="1.4" fill="#e9b5ab" opacity="0.8" />

                <g transform={`translate(${gaze.x * 4.6} ${gaze.y * 2.8})`}>
                  <circle cx={eye.cx} cy="76" r="5.6" fill="url(#av-iris)" />
                  {/* striations: what stops an iris looking like a button */}
                  <g stroke="#0d3f6b" strokeWidth="0.5" opacity="0.55">
                    {Array.from({ length: 14 }, (_, i) => {
                      const a = (i / 14) * Math.PI * 2;
                      return (
                        <line
                          key={i}
                          x1={eye.cx + Math.cos(a) * 2.4}
                          y1={76 + Math.sin(a) * 2.4}
                          x2={eye.cx + Math.cos(a) * 5.2}
                          y2={76 + Math.sin(a) * 5.2}
                        />
                      );
                    })}
                  </g>
                  <circle cx={eye.cx} cy="76" r="5.6" fill="none" stroke="#08263f" strokeWidth="1.1" opacity="0.85" />
                  <circle cx={eye.cx} cy="76" r="2.3" fill="#050a12" />
                  <circle cx={eye.cx - 2} cy="73.9" r="1.8" fill="#ffffff" opacity="0.96" />
                  <circle cx={eye.cx + 2.4} cy="78.4" r="0.9" fill="#bfefff" opacity="0.7" />
                </g>

                <ellipse className={s.lid} cx={eye.cx} cy="76" rx="11.4" ry="7" fill="url(#av-skin)" />
                <path
                  d={`M${eye.cx - 11} 73.6c4-5 18-5 22 0`}
                  fill="none"
                  stroke="#140f1c"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                />
                <Lashes cx={eye.cx} side={eye.side} />
                <path
                  d={`M${eye.cx - 9.6} 81c3.4-2.8 15.8-2.8 19.2 0`}
                  fill="none"
                  stroke="#8a6055"
                  strokeWidth="1"
                  opacity="0.6"
                />
                <path
                  d={`M${eye.cx - 11} 69.5c4-4.4 18-4.4 22 0`}
                  fill="none"
                  stroke="#c9998a"
                  strokeWidth="1"
                  opacity="0.55"
                />
              </g>
            ))}

            {/* nose: shadow down one side, tip highlight, nostrils, philtrum */}
            <path
              d="M150 76c-2 12-1 18 2 22"
              fill="none"
              stroke="#c2907f"
              strokeWidth="2"
              strokeLinecap="round"
              opacity="0.5"
              filter="url(#av-soft2)"
            />
            <ellipse cx="150" cy="97" rx="6" ry="4" fill="#fff4ec" opacity="0.4" filter="url(#av-soft2)" />
            <path
              d="M143 100c2.6 2.4 5 3.2 7 3.2s4.4-.8 7-3.2"
              fill="none"
              stroke="#b07f70"
              strokeWidth="1.5"
              strokeLinecap="round"
              opacity="0.7"
            />
            <ellipse cx="144.6" cy="99.4" rx="1.5" ry="1" fill="#9d6b5e" opacity="0.7" />
            <ellipse cx="155.4" cy="99.4" rx="1.5" ry="1" fill="#9d6b5e" opacity="0.7" />
            <path d="M148 104v4M152 104v4" stroke="#d0a294" strokeWidth="0.9" opacity="0.5" />

            {/* ── red lips ─────────────────────────────────────────── */}
            <g className={s.mouth} style={{ transformOrigin: "150px 111px" }}>
              <ellipse className={s.mouthOpen} cx="150" cy="112" rx="13" ry="0" fill="#5b1420" />
              <ellipse className={s.teeth} cx="150" cy="109" rx="10" ry="0" fill="#f7fafc" opacity="0.95" />
              <path
                className={s.lipUpper}
                d="M134 109c4-4 7-5.6 9.6-5.6 2.4 0 4 1.6 6.4 1.6s4-1.6 6.4-1.6c2.6 0 5.6 1.6 9.6 5.6-5 2.2-10.4 3-16 3s-11-.8-16-3z"
                fill="url(#av-lip-top)"
              />
              <path
                className={s.lipLower}
                d="M134.6 110.6c5 6.4 25.8 6.4 30.8 0-4 6.6-9.4 9.4-15.4 9.4s-11.4-2.8-15.4-9.4z"
                fill="url(#av-lip-bot)"
              />
              <path
                d="M142 115.6c5 2 11 2 16 0"
                fill="none"
                stroke="#ff9aa2"
                strokeWidth="1.5"
                opacity="0.55"
                strokeLinecap="round"
              />
              <path d="M140 106.6c6-2.2 14-2.2 20 0" fill="none" stroke="#f0868f" strokeWidth="0.9" opacity="0.45" />
              <ellipse cx="150" cy="122" rx="11" ry="3" fill="#c08d7e" opacity="0.35" filter="url(#av-soft2)" />
              <circle cx="134" cy="110" r="1.2" fill="#7d1620" opacity="0.7" />
              <circle cx="166" cy="110" r="1.2" fill="#7d1620" opacity="0.7" />
            </g>

            {/* hair at the front: a side-swept fringe with weight */}
            <path
              d="M150 12c-38 0-60 25-60 60 0 6 1 11 2 16 1-16 4-28 9-36 7 11 21 16 38 16 20 0 37-7 47-19 5 8 8 21 9 39 1-5 2-10 2-16 0-35-9-60-47-60z"
              fill="url(#av-hair2)"
            />
            <path
              d="M96 62c3-31 24-48 52-48"
              fill="none"
              stroke="#a8dcff"
              strokeWidth="1.8"
              opacity="0.7"
              filter="url(#av-glow)"
            />
            <path d="M204 68c-2-28-18-45-38-50" fill="none" stroke="#8fd9ff" strokeWidth="1.3" opacity="0.4" />
            <g stroke="#3b3452" strokeWidth="1.1" fill="none" opacity="0.6" strokeLinecap="round">
              <path d="M104 56c9-18 24-29 40-32" />
              <path d="M112 46c9-13 21-21 34-24" />
              <path d="M190 60c-5-17-16-28-30-33" />
            </g>

            {/* the one piece of hardware: a lit arc at the temple */}
            <g className={s.temple}>
              <path
                d="M196 62c12 5 17 15 16 27"
                fill="none"
                stroke="#9fe4ff"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.8"
              />
              <circle cx="212" cy="91" r="2.6" fill="#bfefff" filter="url(#av-glow)" />
            </g>
          </g>
        </g>

        {/* scanlines: enough to texture, not enough to mud */}
        <g className={s.scan} opacity="0.06">
          {Array.from({ length: 50 }, (_, i) => (
            <line key={i} x1="0" y1={i * 4} x2="300" y2={i * 4} stroke="#dff2ff" strokeWidth="0.9" />
          ))}
        </g>
      </g>

      {/* the frame, and the state it carries */}
      <rect className={s.frame} x="2" y="2" width="296" height="196" rx="12" fill="none" strokeWidth="3" />
      <rect
        className={s.frameArc}
        x="2"
        y="2"
        width="296"
        height="196"
        rx="12"
        fill="none"
        strokeWidth="3.5"
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray="16 84"
      />
    </svg>
  );
}
